import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { SidebarItemType } from "../types/Sidebar.types";

type Props = {
  item: SidebarItemType;
  level?: number; // depth level, root = 0
};

export default function SidebarItem({ item, level = 0 }: Props) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState<boolean>(false);

  // helper: recursively check if current pathname matches this item or any descendant
  const hasActiveDescendant = useCallback(
    (node: SidebarItemType): boolean => {
      // if node has path, check exact match OR prefix match (so parent paths like /users will match /users/123)
      if (node.path) {
        // exact match
        const exact = matchPath({ path: node.path, end: true }, pathname);
        if (exact) return true;

        // prefix match: consider node.path a parent if not exact (end: false)
        const prefix = matchPath({ path: node.path, end: false }, pathname);
        if (prefix) return true;
      }

      // children
      if (node.children) {
        return node.children.some((c) => hasActiveDescendant(c));
      }

      return false;
    },
    [pathname]
  );

  // compute whether this exact item is active (exact path)
  const isActive = useMemo(() => {
    if (!item.path) return false;
    return !!matchPath({ path: item.path, end: true }, pathname);
  }, [item.path, pathname]);

  // whenever pathname changes, ensure ancestors that contain the active route are expanded
  useEffect(() => {
    const shouldBeOpen = hasActiveDescendant(item);
    setOpen(shouldBeOpen);
  }, [pathname, item, hasActiveDescendant]);

  // Inline indentation — safe (avoids dynamic tailwind class usage).
  const indentPx = level * 12; // 12px per level — tweak as you like

  const labelBaseClasses =
    "flex-1 block py-2 px-3 rounded text-sm truncate";

  const activeClasses = "bg-white text-kvs";
  const ancestorOpenClasses = "bg-kvs text-white font-medium";
  const normalClasses = "text-white hover:bg-kvs";

  const containerClasses = isActive ? activeClasses : open ? ancestorOpenClasses : normalClasses;

  return (
    <div>
      <div
        className={`flex items-center justify-between ${containerClasses}`}
        style={{ paddingLeft: indentPx }}
      >
        {/* Label (link if path exists, otherwise a non-link button) */}
        {item.path ? (
          <Link
            to={item.path}
            className={`${labelBaseClasses}`}
            onClick={() => {
              // allow navigation to occur; caret button controls expand only
            }}
          >
            {item.label}
          </Link>
        ) : (
          <button
            className={`${labelBaseClasses} text-left w-full`}
            onClick={() => setOpen((s) => !s)}
          >
            {item.label}
          </button>
        )}

        {/* Chevron / toggle */}
        {item.children && item.children.length > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setOpen((s) => !s);
            }}
            aria-expanded={open}
            aria-label={open ? "Collapse submenu" : "Expand submenu"}
            className="p-2 rounded hover:bg-gray-200 mr-2"
            style={{ marginLeft: 8 }}
          >
            {/* simple chevron */}
            <span className="text-xs" aria-hidden>
              {open ? "▾" : "▸"}
            </span>
          </button>
        )}
      </div>

      {/* Children */}
      {open && item.children && (
        <div role="group" aria-label={`${item.label} submenu`} className="mt-1">
          {item.children.map((child) => (
            <SidebarItem key={child.id ?? child.label} item={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  );
}






